import { Cookie } from '@mui/icons-material'
import { createAsyncThunk, createReducer, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { DefaultApi, IntegrationGroup, TokenPairResponse } from '@sendhub/integrations/api-client-typescript-axios'
import axios, { AxiosRequestConfig, isAxiosError } from 'axios'
import Cookies from 'js-cookie'
import { integrationsApi } from '../../services/integrations-api'
import { RootState } from '../root'
import { UserProfile, UserSettings } from '@sendhub/api-client-typescript-axios'
import { INTEGRATIONS_BASE_URL, SENDHUB_BASE_URL } from '../../../utils/config'
import { sendhubSessionKeyHeader } from '../../../utils/session-key'
import { bearerTokenHeader, clearTokens } from '../../../utils/token-store'

export const AUTH_FEATURE_KEY = 'auth'

export interface AuthState {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  isLoggedIn?: boolean
  error?: Error | string | null
  ssoLoadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  ssoProfile?: UserProfile
  ssoSettings?: UserSettings
}

export const initialAuthState: AuthState = {
  loadingStatus: 'not loaded',
  isLoggedIn: false,
  error: null,
  ssoLoadingStatus: 'not loaded',
}

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(login())
 * }, [dispatch]);
 * ```
 */
export const login = createAsyncThunk<TokenPairResponse, { username: string; password: string; both: boolean }>(
  'auth/login',
  async ({ username, password, both }, thunkAPI) => {
    return integrationsApi.login({ username, password, both }).then((r) => r.data)
  }
)

export const logout = createAsyncThunk('auth/logout', async (_, thunkAPI) => {
  await integrationsApi.logout()
  clearTokens()
  Cookies.remove('refreshToken')
})

export const checkSso = createAsyncThunk(
  'auth/checkSso',
  async (_, thunkAPI): Promise<{ profile: UserProfile; settings: UserSettings }> => {
    try {
      // The proxy forwards our headers to SendHub, which authenticates from the
      // session key — the cookie itself can't reach this origin. See
      // utils/session-key.ts. /proxy/ is also covered by the API's own JWT
      // security, hence the access token as well — see utils/token-store.ts.
      const init: RequestInit = {
        headers: { ...sendhubSessionKeyHeader(), ...bearerTokenHeader() },
      }
      const profileResp = await fetch(`${INTEGRATIONS_BASE_URL}/proxy/${SENDHUB_BASE_URL}/api/v1/profile`, init)
      const settingsResp = await fetch(`${INTEGRATIONS_BASE_URL}/proxy/${SENDHUB_BASE_URL}/api/v1/settings`, init)

      if (!profileResp.ok) throw new Error(`Failed to fetch SSO profile: ${profileResp.status} ${profileResp.statusText}`)
      if (!settingsResp.ok) throw new Error(`Failed to fetch SSO settings: ${settingsResp.status} ${settingsResp.statusText}`)
      const profile: UserProfile = await profileResp.json()
      const settings: UserSettings = await settingsResp.json()
      console.log({ profile, settings })
      return { profile, settings }
    } catch (error) {
      console.error('Failed to fetch SSO profile/settings', error)
      throw error
    }
  }
)

export const doSso = createAsyncThunk('auth/doSso', async (_, thunkAPI): Promise<void> => {
  await integrationsApi.sso()
})

export const checkAuth = createAsyncThunk('auth/checkAuth', async (_, thunkAPI): Promise<void> => {
  // return integrationsApi.listIntegrationGroups().then((r) => r.data)
  try {
    await integrationsApi.listIntegrationGroups()
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      clearTokens()
      Cookies.remove('refreshToken')
    }

    throw error
  }
})

export const refreshToken = createAsyncThunk<TokenPairResponse>('auth/refreshToken', async (_, thunkAPI) => {
  return integrationsApi.refreshToken().then((r) => {
    console.log('resp.headers.toJSON():')
    return r.data
  })
})

// const setAuthTokenReducer = createReducer()

export const authSlice = createSlice({
  name: AUTH_FEATURE_KEY,
  initialState: initialAuthState,
  reducers: {
    setIsLoggedIn(state, action: PayloadAction<boolean>) {
      state.isLoggedIn = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state: AuthState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(login.fulfilled, (state: AuthState, action: PayloadAction<TokenPairResponse>) => {
        state.isLoggedIn = true
        state.loadingStatus = 'loaded'
      })
      .addCase(login.rejected, (state: AuthState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })

      .addCase(logout.pending, (state: AuthState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(logout.fulfilled, (state: AuthState) => {
        state.isLoggedIn = false
        state.loadingStatus = 'loaded'
      })
      .addCase(logout.rejected, (state: AuthState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })

      .addCase(refreshToken.pending, (state: AuthState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(refreshToken.fulfilled, (state: AuthState, action: PayloadAction<TokenPairResponse>) => {
        state.isLoggedIn = true
        state.loadingStatus = 'loaded'
      })
      .addCase(refreshToken.rejected, (state: AuthState, action) => {
        state.isLoggedIn = false
        state.loadingStatus = 'error'
        state.error = action.error.message
      })

      .addCase(checkAuth.pending, (state: AuthState) => {
        console.log('checkAuth.pending')
        state.loadingStatus = 'loading'
      })
      .addCase(checkAuth.fulfilled, (state: AuthState) => {
        console.log('checkAuth.fulfilled')
        state.isLoggedIn = true
        state.loadingStatus = 'loaded'
      })
      .addCase(checkAuth.rejected, (state: AuthState, action) => {
        console.log('checkAuth.rejected, error:', action.error)
        state.isLoggedIn = false
        state.loadingStatus = 'error'
        state.error = action.error.message
      })

      .addCase(doSso.pending, (state: AuthState) => {
        console.log('doSso.pending')
        state.loadingStatus = 'loading'
      })
      .addCase(doSso.fulfilled, (state: AuthState) => {
        console.log('doSso.fulfilled')
        state.isLoggedIn = true
        state.loadingStatus = 'loaded'
      })
      .addCase(doSso.rejected, (state: AuthState, action) => {
        console.log('doSso.rejected, error:', action.error)
        state.loadingStatus = 'error'
        state.error = action.error.message
      })

      .addCase(checkSso.pending, (state: AuthState) => {
        console.log('checkSso.pending')
        state.ssoLoadingStatus = 'loading'
      })
      .addCase(checkSso.fulfilled, (state: AuthState, { payload: { profile, settings } }) => {
        console.log('checkSso.fulfilled')
        state.ssoProfile = profile
        state.ssoSettings = settings
        state.ssoLoadingStatus = 'loaded'
      })
      .addCase(checkSso.rejected, (state: AuthState, action) => {
        console.log('checkSso.rejected, error:', action.error)
        state.ssoLoadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const authReducer = authSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(authActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const authActions = authSlice.actions

declare global {
  interface Window {
    authActions: ReturnType<(typeof authSlice)['actions']['setIsLoggedIn']> extends never
      ? Record<string, unknown>
      : typeof authSlice.actions
  }
}
window.authActions = authActions
export const getAuthState = (rootState: RootState): AuthState => {
  if (typeof rootState !== 'object' || !rootState || !(AUTH_FEATURE_KEY in rootState))
    throw new Error(`${AUTH_FEATURE_KEY} not in rootState!`)

  return rootState[AUTH_FEATURE_KEY]
}

export const selectRefreshToken = createSelector(getAuthState, (state) => state.isLoggedIn)
export const selectLoadingStatus = createSelector(getAuthState, (state) => state.loadingStatus)
export const selectError = createSelector(getAuthState, (state) => state.error)

export const selectSsoLoadingStatus = createSelector(getAuthState, (state) => state.ssoLoadingStatus)
export const selectSsoProfile = createSelector(getAuthState, (state) => state.ssoProfile)
export const selectSsoSettings = createSelector(getAuthState, (state) => state.ssoSettings)
